/**
 * identity-service JWT store (otter-camp-suite).
 *
 * Mirrors dao-suite's `identity-jwt-store` so a DAO member who authenticated at
 * the portal (dao-suite) stays logged in when they navigate to Otter Camp on the
 * shared `.helloworlddao.com` registrable domain (ADR-015 / ADR-024 unified
 * identity).
 *
 * Holds the identity-service ES256 access JWT IN MEMORY ONLY: the token lives in
 * a module-scoped variable, is never written to `localStorage` /
 * `sessionStorage` / a JS-readable cookie, and is therefore cleared on page
 * refresh (XSS cannot exfiltrate it from storage). The session is rehydrated on
 * every load by the bootstrap (`identity-code-bootstrap`), which performs a
 * silent refresh from the httpOnly `hwdao_refresh` cookie
 * (Domain=.helloworlddao.com — sent to `ottercamp.*.helloworlddao.com`).
 *
 * **No-JWT is guest (authz-001-9c).** When NO token is set (a genuine guest, or
 * before the rehydrate populates it), `checkSession()` returns a guest session
 * directly — the legacy oracle-bridge cookie-session fallback was retired ahead
 * of the `dac-001` cutover. The bootstrap runs a silent JWT refresh before the
 * session check, so a logged-in member always has a token here by check time.
 */

/** In-memory identity-service access JWT. Never persisted. */
let identityJwt: string | null = null;

/**
 * Store the identity-service access JWT (after login / token exchange / refresh).
 * Pass null/empty to clear.
 */
export function setIdentityJwt(token: string | null): void {
  identityJwt = token && token.length > 0 ? token : null;
}

/**
 * Get the current identity-service access JWT, or null when none is set
 * (a genuine guest, or before the bootstrap rehydrate populates it —
 * `checkSession()` treats this as guest with zero oracle-bridge calls,
 * per authz-001-9c; there is no cookie-fallback mode to describe here).
 */
export function getIdentityJwt(): string | null {
  return identityJwt;
}

/** Clear the stored identity-service JWT (logout / session expiry). */
export function clearIdentityJwt(): void {
  identityJwt = null;
}

/** True when an identity-service JWT is currently available to send. */
export function hasIdentityJwt(): boolean {
  return identityJwt !== null;
}
